/**
 * Theme Validation Orchestrator
 *
 * Main entry point: accepts raw theme CSS (or null/undefined if the file
 * is missing) and produces a structured validation result. Never fails —
 * all error paths are folded into the result.
 *
 * @module
 */
import { Effect } from "effect"
import type {
  ThemeValidationResult,
  MissingToken,
  InvalidToken,
  ExtraToken,
  ValidToken,
  TokenIssue,
} from "./types"
import { CANONICAL_TOKENS, CANONICAL_MAP, SECTION_LABELS } from "./token-registry"
import { parseThemeCss } from "./parse-theme-css"
import { validateTokenValue } from "./value-validators"

// ── Helpers ──────────────────────────────────────────────────────────────────

const buildAllMissingResult = (summary: string): ThemeValidationResult => {
  const missing: MissingToken[] = CANONICAL_TOKENS.map((def) => ({
    _tag: "MissingToken",
    name: def.name,
    type: def.type,
    section: def.section,
    description: def.description,
  }))
  return {
    isValid: false,
    missing,
    invalid: [],
    extra: [],
    valid: [],
    issues: missing,
    summary,
  }
}

// ── Main Program ─────────────────────────────────────────────────────────────

/**
 * Validate raw theme CSS content against the canonical token registry.
 *
 * Accepts `string | null | undefined`:
 * - null/undefined/empty → all tokens reported as missing
 * - no @theme block     → all tokens reported as missing
 * - valid CSS           → full parse + validation
 *
 * Always succeeds — parse errors are folded into the result.
 */
export const validateThemeCss = (
  input: string | null | undefined,
): Effect.Effect<ThemeValidationResult> => {
  if (input == null || input.trim() === "") {
    return Effect.succeed(
      buildAllMissingResult("No theme CSS provided — file may be missing"),
    )
  }

  return Effect.gen(function* () {
    const parsed = yield* parseThemeCss(input)

    // Build lookup of tokens found in CSS
    const cssTokenMap = new Map(parsed.tokens.map((t) => [t.name, t]))

    const missing: MissingToken[] = []
    const invalid: InvalidToken[] = []
    const valid: ValidToken[] = []

    // Check every canonical token
    for (const def of CANONICAL_TOKENS) {
      const found = cssTokenMap.get(def.name)

      if (!found) {
        missing.push({
          _tag: "MissingToken",
          name: def.name,
          type: def.type,
          section: def.section,
          description: def.description,
        })
        continue
      }

      const error = validateTokenValue(found.value, def.type)

      if (error) {
        invalid.push({
          _tag: "InvalidToken",
          name: def.name,
          value: found.value,
          type: def.type,
          section: def.section,
          message: error,
          line: found.line,
        })
      } else {
        valid.push({
          _tag: "ValidToken",
          name: def.name,
          value: found.value,
          type: def.type,
          section: def.section,
        })
      }
    }

    // Detect extra tokens (present in CSS but not in canonical set)
    const extra: ExtraToken[] = parsed.tokens
      .filter((t) => !CANONICAL_MAP.has(t.name))
      .map((t) => ({
        _tag: "ExtraToken" as const,
        name: t.name,
        value: t.value,
        line: t.line,
      }))

    const issues: TokenIssue[] = [...missing, ...invalid]
    const isValid = issues.length === 0

    const summary = isValid
      ? extra.length > 0
        ? `Theme valid (${valid.length} tokens). ${extra.length} extra token(s) detected.`
        : `Theme valid — all ${valid.length} tokens present and correct.`
      : `Theme invalid: ${missing.length} missing, ${invalid.length} invalid. ${extra.length} extra.`

    return {
      isValid,
      missing,
      invalid,
      extra,
      valid,
      issues,
      summary,
    } satisfies ThemeValidationResult
  }).pipe(
    Effect.catchTag("ThemeParseError", (e) =>
      Effect.succeed(buildAllMissingResult(`Failed to parse theme CSS: ${e.message}`)),
    ),
  )
}

// ── AI Formatter ─────────────────────────────────────────────────────────────

const VALUE_HINTS: Record<string, string> = {
  "css-color": "Use hex (#fff, #ffffff), rgb(), or hsl().",
  "css-length": "Use a number with unit (e.g. 1rem, 16px) or 0.",
  "font-stack": 'Use a comma-separated list ending with a generic family (e.g. "Inter", system-ui, sans-serif).',
  "font-provider-var": 'Fonts are now self-hosted via Astro\'s Google Font Provider. Set the value to var(--font-provider-heading) or var(--font-provider-body). The font provider must also be configured in astro.config.mjs.',
  "css-shadow": "Use offset + blur + color (e.g. 0 1px 3px rgb(0 0 0 / 0.08)).",
}

/**
 * Group an array of items by a key function, preserving insertion order.
 */
const groupBy = <T>(items: readonly T[], key: (item: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>()
  for (const item of items) {
    const k = key(item)
    const arr = map.get(k)
    if (arr) arr.push(item)
    else map.set(k, [item])
  }
  return map
}

/**
 * Format a validation result as a structured prompt section for AI consumption.
 * The output is scenario-aware — different failure modes produce different
 * messages so the AI can take the right corrective action.
 */
export const formatForAi = (result: ThemeValidationResult): string => {
  // ── Fully valid, no extras ──
  if (result.isValid && result.extra.length === 0) {
    return "Theme is valid. All 22 tokens are present with correct values."
  }

  // ── Valid but has extras ──
  if (result.isValid) {
    const lines = [
      `Theme is valid. ${result.extra.length} non-standard ${result.extra.length === 1 ? "token was" : "tokens were"} found that ${result.extra.length === 1 ? "is" : "are"} not part of the canonical theme:`,
      "",
    ]
    for (const t of result.extra) {
      lines.push(`  ${t.name}: ${t.value} (line ${t.line})`)
    }
    return lines.join("\n")
  }

  // ── All tokens missing (file absent, empty, or unparseable) ──
  const isAllMissing = result.missing.length === CANONICAL_TOKENS.length
    && result.invalid.length === 0

  if (isAllMissing) {
    const lines = [
      "The theme.css file is missing or could not be parsed. Generate a complete @theme { } block containing all required tokens.",
      "",
      "Required tokens:",
    ]
    const grouped = groupBy(CANONICAL_TOKENS, (t) => t.section)
    for (const [section, tokens] of grouped) {
      lines.push("")
      lines.push(`  ${SECTION_LABELS[section] ?? section}:`)
      for (const t of tokens) {
        lines.push(`    ${t.name}  (${t.type})  ${t.description}`)
      }
    }
    return lines.join("\n")
  }

  // ── Partial issues (some missing, some invalid, maybe extras) ──
  const errorCount = result.missing.length + result.invalid.length
  const lines = [
    `Theme validation found ${errorCount} ${errorCount === 1 ? "error" : "errors"} in the @theme block.`,
  ]

  if (result.missing.length > 0) {
    lines.push("")
    lines.push("Missing tokens — add these with valid values:")
    for (const t of result.missing) {
      lines.push(`  ${t.name}  (${t.type})  ${t.description}`)
    }
  }

  if (result.invalid.length > 0) {
    lines.push("")
    lines.push("Invalid values — fix these:")
    for (const t of result.invalid) {
      lines.push(`  ${t.name} (line ${t.line}): "${t.value}" is not a valid ${t.type}. ${VALUE_HINTS[t.type]}`)
    }
  }

  if (result.extra.length > 0) {
    lines.push("")
    lines.push(`Note: ${result.extra.length} non-standard ${result.extra.length === 1 ? "token" : "tokens"} also found (not an error):`)
    for (const t of result.extra) {
      lines.push(`  ${t.name}: ${t.value} (line ${t.line})`)
    }
  }

  return lines.join("\n")
}
